import React from 'react';

import { Box, Flex, Image, Tooltip } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import NoCollaboratorImg from 'assets/images/NoCollaborator.svg';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import useMatchedEmployees from '../../hooks/useMatchedEmployees';

const StyledTextLabel = styled(TextLabel)`
  overflow: hidden;
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
  margin-left: 2px;
`;

const EmployeeListItem = ({
  displayName,
  profilePhoto,
  id,
  email = '',
  isSelected = false,
  onClick,
  isDisabled,
  isClearable,
  tooltipLabel = '',
  cursor = 'pointer',
  companyId,
  companyName,
  workEmail,
  personalEmail,
}: {
  displayName: string;
  profilePhoto: string;
  id: string;
  email: string;
  isSelected: boolean;
  onClick: (ev) => void;
  isDisabled: boolean;
  isClearable: boolean;
  tooltipLabel?: string;
  cursor?: string;
  companyId?: string;
  companyName?: string;
  workEmail?: string;
  personalEmail?: string;
}) => {
  const displayEmail = calculateDisplayEmail({
    email,
    workEmail,
    personalEmail,
  });
  return (
    <Tooltip label={tooltipLabel}>
      <Flex
        cursor={cursor}
        key={id}
        alignItems="center"
        justifyContent="space-between"
        height="60px"
        p="12px"
        mt={1}
        borderRadius={4}
      >
        <Flex alignItems="center">
          <Avatar size={24} name={displayName} src={profilePhoto} />
          <Flex ml="12px" flexDir="column">
            <TextBodyMedium cursor="pointer" color="inherit">
              {displayName}
            </TextBodyMedium>

            <StyledTextLabel color="text.g2" cursor="pointer">
              {companyId && companyName ? `${displayEmail} (${companyId}, ${companyName})` : displayEmail}
            </StyledTextLabel>
          </Flex>
        </Flex>
        <Flex>
          {/* In fireFox , employee select popover closes itself if isDisabled value changes true from false. i.e. why we are using multiple checkbox here. It could be a bug in chakra library. */}
          {isClearable && isSelected && <Checkbox isChecked onChange={onClick} isDisabled={false} />}
          {!isClearable && isSelected && <Checkbox isChecked onChange={onClick} isDisabled />}
          {!isSelected && <Checkbox onChange={onClick} isDisabled={isDisabled} />}
        </Flex>
      </Flex>
    </Tooltip>
  );
};

const EmptyEmployeeData = ({ emptyText }) => (
  <Box py={2} display="flex" gap={4} flexDirection="column" justifyContent="center" alignItems="center">
    {/* @ts-ignore */}
    <Image height="100px" src={NoCollaboratorImg} alt="NoCollaboratorImg" />
    <TextBodyMedium bold>{emptyText}</TextBodyMedium>
  </Box>
);

const FeedbackSearchEmployees = ({
  employees = [],
  setEmployees,
  atLeastOneEmployeeToBeSelected = false,
  searchText,
}) => {
  const matchedEmployees = useMatchedEmployees({ selectedEmployees: employees, searchText });

  const handleEmployeeClick = ({ emp: employee }) => {
    const isEmployeeAlreadyPresent = employees.findIndex(({ id }) => id === employee.id) > -1;

    if (isEmployeeAlreadyPresent && employees.length === 1 && atLeastOneEmployeeToBeSelected) return;

    if (isEmployeeAlreadyPresent) setEmployees(employees.filter(({ id }) => id !== employee.id));
    else setEmployees([{ ...employee, isSelected: true }, ...employees]);
  };

  return (
    <Box>
      <Flex flexDirection="column" alignItems="stretch">
        {matchedEmployees?.length === 0 ? <EmptyEmployeeData emptyText="No users found" /> : null}

        {matchedEmployees?.map((emp) => {
          const onClick = () => {
            handleEmployeeClick({ emp });
          };
          return (
            <EmployeeListItem
              key={emp.id}
              {...emp}
              onClick={onClick}
              isDisabled={false}
              /* following condition stops last employee to be cleared but lets user select the employee if no employee is selected */
              isClearable={atLeastOneEmployeeToBeSelected ? employees.length !== 1 : true}
            />
          );
        })}
      </Flex>
    </Box>
  );
};

export default FeedbackSearchEmployees;
